import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import { ButtonComponent } from '@shared/components/button';
import { ModalService } from '@shared/components/modal';
import { NatureServiceConnect } from '../liste-clients.interface';
import { ListeClientsService } from '../liste-clients.service';
import { ConnectFamilleComponent } from './connect-famille/connect-famille.component';
import {
  DialogTypeConnectComponent,
  DialogTypeConnectData,
  DialogTypeConnectOutput,
} from './dialog-type-connect/dialog-type-connect.component';

// Un service (famille d'étiquettes, donc une API) et ses types connus. Le type d'une famille
// découverte vient de l'endpoint qui l'a renvoyée ; celui d'un service ajouté ici, du choix fait à
// sa création. Une API est ITR (partenaires, comme TempsReel), client (applications, comme Crm) ou
// front (fronts web, comme VenteWeb) : l'écran ne crée jamais de famille mixte. Il en affiche une si le store en porte une, cas
// seulement transitoire (une API qui change de type)
export interface FamilleConnect {
  nom: string;
  natures: NatureServiceConnect[];
}

// Carte Tick&Live Connect : paramétrage des connexions aux API, une sous-section par service
// (famille d'étiquettes {type}:{env}). Les services sont découverts dans TOUS les environnements
// du store : un service connu ailleurs s'affiche vide ici, avec son type, pour y ajouter
// directement sa première ligne. Un service ajouté ici ne sera persisté qu'avec sa première ligne
@Component({
  selector: 'app-connect',
  imports: [ButtonComponent, ConnectFamilleComponent],
  templateUrl: './connect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './connect.component.scss',
})
export class ConnectComponent {
  private listeClientsService = inject(ListeClientsService);
  private modal = inject(ModalService);

  // Types ajoutés localement, pas encore persistés (aucune ligne enregistrée), avec le type
  // choisi à la création : il décide du bouton d'ajout de la section tant qu'elle est vide
  private famillesLocales = signal<DialogTypeConnectOutput[]>([]);

  private demande = computed(() => this.listeClientsService.environnementSelectionne());

  private famillesChargees = toSignal(
    toObservable(this.demande).pipe(
      filter(Boolean),
      // Un service existe dès qu'il porte des partenaires ITR, des applications client OU des fronts
      switchMap(() =>
        combineLatest([
          this.listeClientsService.recupererFamillesConnect$(),
          this.listeClientsService.recupererFamillesApplicationsConnect$(),
          this.listeClientsService.recupererFamillesFrontsConnect$(),
        ]).pipe(map(([partenaires, applications, fronts]) => fusionnerFamilles(partenaires, applications, fronts))),
      ),
    ),
    { initialValue: [] as FamilleConnect[] },
  );

  familles = computed<FamilleConnect[]>(() => {
    const chargees = this.famillesChargees();
    const locales = this.famillesLocales()
      .filter(locale => !chargees.some(chargee => chargee.nom.toLowerCase() === locale.nom.toLowerCase()))
      .map(locale => ({ nom: locale.nom, natures: [locale.nature] }));
    return [...chargees, ...locales];
  });

  constructor() {
    // Les types non persistés n'ont de sens que dans l'environnement où ils ont été créés
    effect(() => {
      this.demande();
      this.famillesLocales.set([]);
    });
  }

  ajouterType() {
    const data: DialogTypeConnectData = { typesExistants: this.familles().map(famille => famille.nom) };

    // `openForm` : le clic sur le fond ne referme pas un formulaire en cours de
    // saisie, et la fermeture se consomme par `closed` (et non `afterClosed()`)
    this.modal
      .openForm<DialogTypeConnectOutput, DialogTypeConnectData>(DialogTypeConnectComponent, { data })
      .closed.subscribe(service => {
        if (service) {
          this.famillesLocales.set([...this.famillesLocales(), service]);
        }
      });
  }
}

// Réunit les services à partenaires ITR, à applications client et à fronts, chacun avec le type
// de l'endpoint qui l'a renvoyé. Insensible à la casse, comme les étiquettes du store : un service
// présent dans plusieurs listes porte plusieurs types
export function fusionnerFamilles(partenaires: string[], applications: string[], fronts: string[]): FamilleConnect[] {
  const familles: FamilleConnect[] = [];

  const ajouter = (nom: string, nature: NatureServiceConnect) => {
    const existante = familles.find(famille => famille.nom.toLowerCase() === nom.toLowerCase());
    if (!existante) {
      familles.push({ nom, natures: [nature] });
    } else if (!existante.natures.includes(nature)) {
      existante.natures.push(nature);
    }
  };

  partenaires.forEach(nom => ajouter(nom, 'partenaire'));
  applications.forEach(nom => ajouter(nom, 'application'));
  fronts.forEach(nom => ajouter(nom, 'front'));

  return familles.sort((a, b) => a.nom.localeCompare(b.nom));
}
